// BusTracker Pro — guidage de tournée et enregistrement de trajet.
// Ce module garde l'état de navigation et tout ce qui touche au DOM ; la géométrie, les
// manœuvres, le routage, la voix et le verrou d'écran vivent dans des modules dédiés.

import L from './leaflet-global.js';
import { haversineMeters } from './geo.js';
import { MANEUVER_ICONS, translateInstruction } from './maneuvers.js';
import { setRoutingLogger, matchTrace } from './routing.js';
import { initVoice, speak, cancelSpeech } from './voice.js';
import {
    setWakeLockGuard, requestWakeLock, releaseWakeLock, installWakeLockListeners
} from './wakelock.js';
import {
    initGuidance, resetNavState, buildNavigationRoute, updateGuidance, confirmStop, skipStop, nav
} from './guidance.js';
import { createBasemapLayer, downloadBasemap, basemapStatus } from './basemap.js';
import {
    listTrips, getTrip, saveTrip, deleteTrip, markTripSynced, requestPersistentStorage
} from './storage.js';
import {
    ApiAuthError, ApiOfflineError, getCode, setCode, newSyncId,
    apiListTrips, apiGetTrip, apiPutTrip, apiDeleteTrip, mergeTripLists
} from './api.js';
import { registerServiceWorker, isUpdateWaiting, applyUpdate, checkForUpdate } from './pwa.js';


// Variables globales
let map = null;
let busMarker = null;
let routePolyline = null;
let plannedRoutePolyline = null;

let watchId = null;
let isTracking = false;
let currentPosition = null;

let pathCoordinates = [];
let markedStops = [];
// Déclarés ICI et pas ailleurs. Ils vivaient en locaux morts dans guidance.js, et app.js les
// lisait donc comme des globales implicites : la lecture de lastFix avant toute affectation
// levait une ReferenceError au PREMIER fix GPS de tout appareil qui ne fournit pas
// coords.speed — ce qui interrompait updatePosition() avant l'enregistrement du point, le
// marqueur du bus et le guidage.
let currentSpeedKmh = 0;   // sert au seuil d'annonce vocale (plus vite = prévenir plus tôt)
let lastFix = null;        // fix précédent, pour recalculer la vitesse si le GPS ne la donne pas

// Précision de chaque point du tracé, en tableau parallèle à pathCoordinates. Sans elle, le
// recalage ne peut pas dire à OSRM quelle tolérance accorder point par point.
let pathAccuracy = [];
let rejectedFixes = 0;     // pour ne journaliser qu'une fois sur dix
let startDeadline = 0;     // échéance de repli pour la pose du point de départ
let bestStartFix = null;   // meilleur fix vu en attendant un fix acceptable

// Un fix au-delà de ce seuil dessine un crochet dans la rue voisine ; en dessous du seuil de
// déplacement, c'est du tremblement et non du mouvement.
const GPS_MAX_ACCURACY_M = 25;
const GPS_MIN_MOVE_M = 4;
// Passé ce délai sans fix acceptable, on pose le départ sur le meilleur vu : un autobus
// démarre au garage, précisément là où le premier fix est mauvais, et sans point de départ il
// n'y a pas de trajet du tout.
const START_FIX_DEADLINE_MS = 15000;

// Deux gardes pour deux erreurs différentes : le doigt qui rebondit, et le chauffeur qui n'a
// pas vu que son premier appui avait fonctionné.
const STOP_TAP_DEBOUNCE_MS = 1500;
const STOP_MIN_SPACING_M = 15;
let lastStopTapTs = 0;

// Sauter un arrêt se fait en DEUX appuis : un arrêt sauté par erreur, ce sont des enfants
// laissés sur le trottoir. Passé ce délai sans confirmation, l'intention est retombée.
const SKIP_ARM_MS = 5000;
let skipArmed = false;
let skipArmTimer = null;

// Le bus ne doit JAMAIS être caché par un bouton. On mesure ce qui recouvre réellement la
// carte plutôt que de deviner un pourcentage : un réglage calé sur un téléphone retombe sous
// un bouton sur le suivant, et il ignore la pancarte qui apparaît en cours de route.
const MAP_OBSTACLE_IDS = ['app-header', 'navigation-panel', 'mark-stop-container', 'bottom-panel'];
const CLEAR_BAND_MIN_PX = 120;
let rawGeneratedInstructions = "";
let currentMode = null; // 'record' ou 'navigate'


// Éléments UI
const mapContainer = document.getElementById('map');
const toggleBtn = document.getElementById('toggle-tracking-btn');
const markStopBtn = document.getElementById('mark-stop-btn');
const exportBtn = document.getElementById('export-btn');
const saveJsonBtn = document.getElementById('save-json-btn');
const postTripActions = document.getElementById('post-trip-actions');
const logList = document.getElementById('log-list');
const stopCounter = document.getElementById('stop-counter');
const statusDot = document.getElementById('status-dot');
const statusText = document.getElementById('status-text');
const headerTitle = document.getElementById('header-title');
const fileInput = document.getElementById('file-input');
const navPanel = document.getElementById('navigation-panel');
const navText = document.getElementById('next-instruction-text');
const markStopContainer = document.getElementById('mark-stop-container');

// Éléments du bandeau de guidage
const navTurn = document.getElementById('nav-turn');
const navArrow = document.getElementById('nav-arrow');
const navDistance = document.getElementById('nav-distance');
const navStreet = document.getElementById('nav-street');
const navStop = document.getElementById('nav-stop');
const navStopDist = document.getElementById('nav-stop-dist');
const navStopLabel = document.getElementById('nav-stop-label');
const navStopAheadLabel = document.getElementById('nav-stop-ahead-label');
const navStopAheadDist = document.getElementById('nav-stop-ahead-dist');
const bottomPanel = document.getElementById('bottom-panel');
const btnSkipStop = document.getElementById('btn-skip-stop');
const btnTts = document.getElementById('btn-tts');
const ttsIcon = document.getElementById('tts-icon');

// Éléments du menu principal
const mainMenu = document.getElementById('main-menu');
const btnModeRecord = document.getElementById('btn-mode-record');
const btnModeNav = document.getElementById('btn-mode-nav');
const btnBackMenu = document.getElementById('btn-back-menu');

// Modal UI
const instructionsModal = document.getElementById('instructions-modal');
const closeBtn = document.getElementById('close-modal-btn');
const instructionsContent = document.getElementById('generated-instructions-content');
const downloadBtn = document.getElementById('download-txt-btn');

// Éléments UI du Modal JSON
const jsonModal = document.getElementById('json-modal');
const closeJsonBtn = document.getElementById('close-json-btn');
const jsonOutput = document.getElementById('json-output');
const copyJsonBtn = document.getElementById('copy-json-btn');
const forceDownloadBtn = document.getElementById('force-download-btn');


// Le serveur public OSRM est notre point de défaillance unique : il tombe et il
// limite le débit par IP. On essaie donc dans l'ordre deux instances OSRM (même API,
// aucun code spécifique), puis Valhalla — un moteur différent, qui ne tombe donc pas
// en même temps.

// --- Géométrie ---


// Distance point <-> segment, en projection équirectangulaire locale : à l'échelle d'un
// segment de rue l'erreur est négligeable, et c'est bien moins coûteux qu'un vrai calcul
// géodésique répété des centaines de fois par fix GPS.

// Balayage limité à une fenêtre autour du dernier point le plus proche : un trajet
// d'autobus compte des milliers de points de tracé, et un balayage complet à chaque fix
// ferait ramer le téléphone.

// Arrondi progressif : amortit le tremblement GPS sans figer l'affichage.


// --- Icônes de manœuvre (SVG inline, viewBox 24x24, tracé au stroke) ---

// Un seul descripteur pour l'icône ET le texte : la modale d'export et le bandeau de
// guidage doivent rester d'accord sur la façon de nommer une manœuvre.


// =====================================================================
//  VERROU D'ÉCRAN
// =====================================================================
// Sans ça l'écran s'éteint en pleine tournée et le guidage disparaît. Le navigateur
// relâche le verrou dès que l'onglet passe en arrière-plan, d'où le réarmement sur
// visibilitychange et au toucher.


// =====================================================================
//  ANNONCES VOCALES
// =====================================================================


// Seuil d'annonce lié à la vitesse : plus on roule vite, plus il faut prévenir tôt.
// Formule reprise de GPS-Nav-HTML, resserrée pour la conduite en secteur résidentiel.

// Deux paliers pour les virages : une fois au seuil lié à la vitesse, une fois de près.

// Annonce unique par cible, au franchissement du seuil.


async function initMap() {
    // Initialisation centrée sur Drummondville par défaut
    // maxZoom : sans plafond, getMaxZoom() vaut Infinity et un écartement de doigts zoome
    // indéfiniment dans des données z15 grossies, jusqu'à une bouillie de traits épais.
    map = L.map('map', { zoomControl: false, maxZoom: 18 }).setView([45.88, -72.48], 13);
    
    // Déplacer les contrôles de zoom pour ne pas être cachés par le panneau
    L.control.zoom({ position: 'topright' }).addTo(map);

    // Fond vectoriel PMTiles : un seul fichier régional, lu en HTTP Range ou hors ligne
    // depuis IndexedDB. Remplace les tuiles CARTO, dont les conditions d'utilisation
    // interdisent le téléchargement pour usage hors ligne.
    try {
        const { layer, offline } = await createBasemapLayer();
        layer.addTo(map);
        if (offline) logEvent("Carte", "Fond de carte hors ligne actif.");
    } catch (e) {
        console.error('fond de carte indisponible', e);
        logEvent("Carte", "Fond de carte indisponible — le guidage fonctionne quand même.");
    }

    // Création des lignes de tracé
    routePolyline = L.polyline([], {color: '#3b82f6', weight: 6, opacity: 0.8, lineCap: 'round'}).addTo(map);
    plannedRoutePolyline = L.polyline([], {color: '#10b981', weight: 5, opacity: 0.6, dashArray: '10, 10'}).addTo(map);

    // S'assurer que la carte prend bien sa taille
    setTimeout(() => { map.invalidateSize(); }, 300);
}

window.addEventListener('resize', () => { if(map) map.invalidateSize(); });

// Décalage, en pixels, entre le centre de l'écran et le centre de la bande de carte réellement
// visible. Recalculé à CHAQUE fix GPS : quatre getBoundingClientRect une fois par seconde ne
// coûtent rien, et ça supprime toute la classe de bugs « on a oublié d'invalider le cache »
// (changement de mode, rotation, apparition de la pancarte).
function clearBandOffset() {
    const box = mapContainer.getBoundingClientRect();
    let top = box.top, bottom = box.bottom;

    for (const id of MAP_OBSTACLE_IDS) {
        const el = document.getElementById(id);
        if (!el) continue;
        const r = el.getBoundingClientRect();
        if (r.height === 0 || r.width === 0) continue;   // masqué : ne mange rien
        // Ancré en haut ou en bas ? On le déduit de la mesure, pour ne pas avoir à maintenir
        // une liste par mode : en navigation le bouton ARRÊT est masqué et le bandeau occupe
        // le haut, et ça se règle tout seul.
        if (r.top - box.top < box.bottom - r.bottom) top = Math.max(top, r.bottom);
        else bottom = Math.min(bottom, r.top);
    }

    // Bande trop mince (petit écran avec bandeau ET pancarte) : mieux vaut un bus mal centré
    // qu'un bus poussé hors de l'écran.
    if (bottom - top < CLEAR_BAND_MIN_PX) return 0;
    return (top + bottom) / 2 - (box.top + box.bottom) / 2;
}

function centerOnBus(lat, lng, zoom) {
    const z = zoom === undefined ? map.getZoom() : zoom;
    const p = map.project([lat, lng], z);
    // Le bus doit apparaître au centre de la bande libre : le centre de la carte se déplace
    // donc en sens inverse.
    p.y -= clearBandOffset();
    const target = map.unproject(p, z);
    if (zoom === undefined) map.panTo(target);
    else map.setView(target, z);
}

// --- LOGIQUE DU MENU PRINCIPAL ---

btnModeRecord.addEventListener('click', () => {
    currentMode = 'record';
    headerTitle.textContent = "Enregistrement";
    mainMenu.classList.add('-translate-y-full'); // Animation de masquage
    markStopContainer.classList.remove('hidden');
    btnSkipStop.classList.add('hidden');              // sauter un arrêt n'existe qu'en navigation
    bottomPanel.classList.replace('pt-4', 'pt-24');   // place au bouton ARRÊT à cheval
    if(map) { setTimeout(() => { map.invalidateSize(); }, 300); }
});

btnModeNav.addEventListener('click', () => {
    // L'écran s'ouvre AVANT toute requête, et la liste locale se peint tout de suite : un
    // await sur le serveur ici rendrait le bouton inerte pendant le délai d'attente, au
    // moment précis où le chauffeur veut partir.
    tripScreen.classList.remove('hidden');
    tripScreen.classList.add('flex');
    renderTripList();
    syncServerTrips();
});

btnBackMenu.addEventListener('click', () => {
    // Arrêter le suivi si actif
    if (isTracking) toggleBtn.click();
    
    // Nettoyer la carte
    pathCoordinates = [];
    markedStops = [];
    nav.waypoints = [];
    routePolyline.setLatLngs([]);
    plannedRoutePolyline.setLatLngs([]);
    if (busMarker) { map.removeLayer(busMarker); busMarker = null; }
    map.eachLayer((layer) => {
        if (layer instanceof L.Marker) map.removeLayer(layer);
    });

    // Réinitialiser l'interface UI
    logList.innerHTML = '<li class="text-slate-400 italic">Appuyez sur "Démarrer" pour commencer.</li>';
    stopCounter.classList.add('hidden');
    navPanel.classList.add('hidden');
    navStop.classList.add('hidden');
    navTurn.classList.remove('hidden');
    btnTts.classList.add('hidden');
    disarmSkip();
    btnSkipStop.classList.add('hidden');
    cancelSpeech();
    releaseWakeLock();
    postTripActions.classList.add('hidden');

    // Sans ça, un second trajet chargé héritait des manœuvres du premier.
    resetNavState();
    
    // Afficher le menu
    mainMenu.classList.remove('-translate-y-full');
    currentMode = null;
});

function logEvent(type, message) {
    const time = new Date().toLocaleTimeString('fr-CA', {hour: '2-digit', minute:'2-digit', second:'2-digit'});
    const li = document.createElement('li');
    li.innerHTML = `<span class="text-slate-400 mr-2">[${time}]</span> <strong class="text-slate-700">${type}</strong>: ${message}`;
    logList.prepend(li);
}

function setStatus(state) {
    if (state === 'active') {
        statusDot.className = "w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse";
        statusText.textContent = "En route";
        statusText.className = "text-xs font-semibold text-emerald-600 uppercase tracking-wide";
        
        toggleBtn.innerHTML = `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z"></path></svg> Terminer`;
        toggleBtn.className = "w-full bg-rose-500 hover:bg-rose-600 active:bg-rose-700 text-white font-bold py-3.5 px-4 rounded-2xl shadow-md transition-all flex items-center justify-center gap-2";
        
        markStopBtn.disabled = false;
        markStopBtn.classList.remove('opacity-50');
        postTripActions.classList.add('hidden');
    } else {
        statusDot.className = "w-2.5 h-2.5 rounded-full bg-slate-400";
        statusText.textContent = "Arrêté";
        statusText.className = "text-xs font-semibold text-slate-500 uppercase tracking-wide";
        
        toggleBtn.innerHTML = `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg> Démarrer`;
        toggleBtn.className = "w-full bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 text-white font-bold py-3.5 px-4 rounded-2xl shadow-md transition-all flex items-center justify-center gap-2";
        
        markStopBtn.disabled = true;
        markStopBtn.classList.add('opacity-50');
        
        if (markedStops.length > 1) {
            postTripActions.classList.remove('hidden');
        }
    }
}

toggleBtn.addEventListener('click', () => {
    if (!isTracking) {
        // DÉMARRAGE
        if (!navigator.geolocation) {
            alert("Votre navigateur ne supporte pas la géolocalisation.");
            return;
        }

        // Réinitialisation de la trace
        pathCoordinates = [];
        pathAccuracy = [];
        rejectedFixes = 0;
        startDeadline = 0;
        bestStartFix = null;
        lastStopTapTs = 0;
        routePolyline.setLatLngs([]);
        markedStops = [];
        stopCounter.classList.add('hidden');
        stopCounter.textContent = "0 arrêts";
        
        // Nettoyage de la carte (sauf ligne prévue)
        map.eachLayer((layer) => {
            if (layer instanceof L.Marker) map.removeLayer(layer);
        });
        busMarker = null;
        logList.innerHTML = '';

        isTracking = true;
        setStatus('active');
        logEvent("Système", "GPS activé. Enregistrement en cours...");
        requestWakeLock();
        // Prononcer ici, juste après le geste de l'utilisateur : iOS refuse la
        // synthèse vocale tant qu'elle n'a pas été déclenchée par une interaction.
        if (currentMode === 'navigate') speak('Guidage activé');

        watchId = navigator.geolocation.watchPosition(updatePosition, handleError, {
            enableHighAccuracy: true,
            maximumAge: 0,
            timeout: 5000
        });
    } else {
        // ARRÊT
        isTracking = false;
        navigator.geolocation.clearWatch(watchId);
        setStatus('inactive');
        logEvent("Système", "Enregistrement terminé.");
        releaseWakeLock();
        cancelSpeech();
        
        // N'afficher les actions de fin de trajet qu'en mode Enregistrement
        if (currentMode === 'record') {
            if (markedStops.length > 1) {
                postTripActions.classList.remove('hidden');
                logEvent("Action requise", "Générez les instructions ou sauvez le trajet.");
            } else {
                logEvent("Attention", "Pas assez d'arrêts marqués pour générer un trajet.");
            }
            snapTraceToStreets();
        }
    }
});

// Recalage du tracé affiché sur les rues, à la fin de l'enregistrement. Jamais bloquant, et
// jamais destructeur : le tracé brut reste à l'écran tant que le recalage n'a pas abouti.
// N'améliore que ce qu'on VOIT — un trajet enregistré reste la liste des arrêts.
function snapTraceToStreets() {
    if (pathCoordinates.length < 2) return;
    const trace = pathCoordinates.map((p, i) => ({ lat: p.lat, lng: p.lng, acc: pathAccuracy[i] }));
    logEvent("Tracé", "Recalage sur les rues…");
    matchTrace(trace)
        .then(snapped => {
            if (snapped && snapped.length > 1) {
                routePolyline.setLatLngs(snapped);
                logEvent("Tracé", "Recalé sur les rues.");
            } else {
                logEvent("Tracé", "Recalage impossible — tracé GPS conservé.");
            }
        })
        .catch(() => logEvent("Tracé", "Recalage impossible — tracé GPS conservé."));
}

function updatePosition(position) {
    const lat = position.coords.latitude;
    const lng = position.coords.longitude;
    
    currentPosition = { lat, lng };

    // Vitesse : coords.speed est en m/s mais souvent absente selon l'appareil, on la
    // recalcule alors depuis le fix précédent. Elle sert au seuil d'annonce vocale.
    const ts = position.timestamp || Date.now();
    const sp = position.coords.speed;
    if (typeof sp === 'number' && Number.isFinite(sp) && sp >= 0) {
        currentSpeedKmh = sp * 3.6;
    } else if (lastFix) {
        const dt = (ts - lastFix.t) / 1000;
        if (dt > 0.5) currentSpeedKmh = haversineMeters(lastFix.lat, lastFix.lng, lat, lng) / dt * 3.6;
    }
    lastFix = { lat: lat, lng: lng, t: ts };

    const accuracy = position.coords.accuracy;
    const precise = !(typeof accuracy === 'number' && accuracy > GPS_MAX_ACCURACY_M);

    // Point de départ. Il est ENREGISTRÉ, contrairement au tracé : un départ posé à 60 m fait
    // partir l'itinéraire de la mauvaise rue. On attend donc un fix acceptable, mais pas
    // indéfiniment — sans point de départ il n'y a pas de trajet du tout.
    if (markedStops.length === 0) {
        if (!startDeadline) startDeadline = Date.now() + START_FIX_DEADLINE_MS;
        if (!bestStartFix || accuracy < bestStartFix.acc) bestStartFix = { lat, lng, acc: accuracy };

        if (precise || Date.now() >= startDeadline) {
            const f = precise ? { lat, lng, acc: accuracy } : bestStartFix;
            markedStops.push({ lat: f.lat, lng: f.lng, type: 'start', label: 'Départ' });
            addMarkerToMap(f.lat, f.lng, 'Départ', 'bg-blue-500');
            logEvent("Système", `Signal GPS accroché (précision ±${Math.round(f.acc)} m).`);
        } else {
            logEvent("Système", `Signal GPS imprécis (±${Math.round(accuracy)} m) — on attend mieux.`);
        }
    }

    // Tracé : on rejette l'imprécis et le sur-place. Le fix continue d'alimenter la position du
    // bus et le guidage — mieux vaut un marqueur approximatif qu'un marqueur figé.
    const last = pathCoordinates[pathCoordinates.length - 1];
    const moved = !last || haversineMeters(last.lat, last.lng, lat, lng) >= GPS_MIN_MOVE_M;
    if (precise && moved) {
        pathCoordinates.push(new L.LatLng(lat, lng));
        pathAccuracy.push(typeof accuracy === 'number' ? accuracy : GPS_MAX_ACCURACY_M);
        routePolyline.setLatLngs(pathCoordinates);
    } else if (!precise) {
        // Une fois puis toutes les dix : un mauvais signal remplirait le journal et masquerait
        // tout le reste, alors qu'un tracé qui n'avance plus sans explication inquiète.
        rejectedFixes++;
        if (rejectedFixes === 1 || rejectedFixes % 10 === 0) {
            logEvent("Tracé", `${rejectedFixes} point(s) écarté(s), signal à ±${Math.round(accuracy)} m.`);
        }
    }

    if (!busMarker) {
        const busHtml = `<div class="bg-blue-600 text-white p-2 rounded-full shadow-lg border-2 border-white"><svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path></svg></div>`;
        const busIcon = L.divIcon({ html: busHtml, className: '', iconSize: [36, 36], iconAnchor: [18, 18] });
        busMarker = L.marker([lat, lng], {icon: busIcon}).addTo(map);
        centerOnBus(lat, lng, 16);
    } else {
        busMarker.setLatLng([lat, lng]);
        centerOnBus(lat, lng);
    }

    // Guidage temps réel : recalculé à chaque fix GPS, entièrement en local à partir de
    // la table de manœuvres chargée une seule fois.
    if (currentMode === 'navigate' && nav.maneuvers.length > 0) {
        updateGuidance(lat, lng);
    }
}

function handleError(error) {
    let msg = "";
    switch(error.code) {
        case error.PERMISSION_DENIED:
            msg = "Accès refusé. Veuillez autoriser le GPS dans votre navigateur.";
            if (isTracking) toggleBtn.click(); // Arrêter l'enregistrement
            break;
        case error.POSITION_UNAVAILABLE:
            msg = "Signal GPS perdu ou indisponible. Êtes-vous à l'intérieur ?";
            break;
        case error.TIMEOUT:
            msg = "Le GPS a mis trop de temps à répondre.";
            break;
        default:
            msg = "Erreur GPS inconnue.";
            break;
    }
    logEvent("Erreur GPS", msg);
    console.warn("Erreur GPS détaillée: ", error.message);
}

markStopBtn.addEventListener('click', () => {
    if (!currentPosition) {
        alert("Position GPS introuvable. Patientez un instant.");
        return;
    }

    const now = Date.now();
    // Anti-rebond : un second appui aussi rapproché est un doigt qui a rebondi, pas une
    // intention. On l'ignore en silence, il n'y a rien à signaler.
    if (now - lastStopTapTs < STOP_TAP_DEBOUNCE_MS) return;

    // Espacement minimal : ici, en revanche, c'est un chauffeur qui n'a pas vu que son
    // premier appui avait fonctionné. Il faut le lui DIRE, pas ignorer en silence.
    const prev = markedStops[markedStops.length - 1];
    if (prev) {
        const d = haversineMeters(prev.lat, prev.lng, currentPosition.lat, currentPosition.lng);
        if (d < STOP_MIN_SPACING_M) {
            logEvent("Arrêt", `Déjà marqué ici (à ${Math.round(d)} m) — ignoré.`);
            flashStopBtn('déjà marqué', false);
            return;
        }
    }
    lastStopTapTs = now;

    const stopCount = markedStops.length; // 0 est le départ, donc length est le # de l'arrêt
    markedStops.push({ lat: currentPosition.lat, lng: currentPosition.lng, type: 'stop', label: `Arrêt ${stopCount}` });

    // Mettre à jour le badge
    stopCounter.classList.remove('hidden');
    stopCounter.textContent = `${stopCount} arrêt(s)`;

    addMarkerToMap(currentPosition.lat, currentPosition.lng, stopCount, 'bg-amber-500 text-amber-950');
    logEvent("Arrêt marqué", `Passagers embarqués (Lat: ${currentPosition.lat.toFixed(4)})`);
    flashStopBtn(`Arrêt ${stopCount}`, true);
});

// La cause première du double-tap est l'absence de retour : jusqu'ici seuls un badge en petit
// et une ligne de journal changeaient. Le bouton se désactive pendant l'accusé de réception,
// ce qui bloque aussi le second appui.
function flashStopBtn(text, ok) {
    const original = markStopBtn.innerHTML;
    markStopBtn.disabled = true;
    markStopBtn.innerHTML = `<span class="text-4xl font-black leading-none">${ok ? '✓' : '!'}</span>`
        + `<span class="text-base font-black uppercase tracking-wide mt-2 px-4 text-center">${text}</span>`;
    try { if (navigator.vibrate) navigator.vibrate(ok ? 50 : [30, 40, 30]); } catch (e) {}
    setTimeout(() => {
        markStopBtn.innerHTML = original;
        // Ne pas réactiver si l'enregistrement s'est arrêté entre-temps : setStatus() est le
        // seul à décider de l'état du bouton hors suivi.
        if (isTracking) markStopBtn.disabled = false;
    }, 1000);
}

function addMarkerToMap(lat, lng, text, colorClass) {
    const html = `<div class="${colorClass} w-8 h-8 rounded-full border-2 border-white shadow-md flex items-center justify-center font-bold text-xs">${text}</div>`;
    const icon = L.divIcon({ html: html, className: '', iconSize: [32, 32], iconAnchor: [16, 16] });
    L.marker([lat, lng], {icon: icon}).addTo(map);
}

// --- GESTION DU FICHIER JSON ---
// Point d'entrée unique du mode Navigation : utilisé par la liste des trajets enregistrés
// comme par l'import d'un fichier JSON.
function startNavigation(stops) {
    if (!stops || !stops.length || !stops[0].lat || !stops[0].lng) {
        alert("Trajet invalide.");
        return;
    }
    currentMode = 'navigate';
    headerTitle.textContent = "Navigation";
    tripScreen.classList.add('hidden');
    mainMenu.classList.add('-translate-y-full'); // Cacher le menu
    markStopContainer.classList.add('hidden');   // Cacher le bouton arrêt
    // Le bouton ARRÊT masqué, les 96 px qui lui étaient réservés ne sont plus que du blanc
    // pris sur la carte. Il ne doit rester en bas que Démarrer/Terminer.
    bottomPanel.classList.replace('pt-24', 'pt-4');
    disarmSkip();
    btnSkipStop.classList.remove('hidden');
    navPanel.classList.remove('hidden');         // Afficher la barre de guidage
    btnTts.classList.remove('hidden');
    navTurn.classList.remove('hidden');
    navStop.classList.add('hidden');
    navArrow.innerHTML = MANEUVER_ICONS['straight'];
    navArrow.dataset.key = 'straight';
    navDistance.textContent = '--';
    navStopAheadLabel.textContent = '\u00a0';
    navStopAheadDist.textContent = '--';
    // navText est désormais la ligne PRINCIPALE du bandeau : c'est elle qui porte le message.
    navText.textContent = "Calcul du trajet…";
    navStreet.textContent = "\u00a0";

    // Charge le trajet et construit la table des manœuvres (asynchrone : plusieurs
    // requêtes de routage si le trajet dépasse 20 arrêts).
    buildNavigationRoute(stops).then(ok => {
        if (!ok) return;
        navText.textContent = "Trajet chargé";
        navStreet.textContent = "Démarrez pour le guidage";
    });

    if (map) { setTimeout(() => { map.invalidateSize(); }, 300); }
}

fileInput.addEventListener('change', (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
        try {
            const data = JSON.parse(event.target.result);
            fileInput.value = "";   // permet de réimporter le même fichier
            // Un fichier importé est aussi enregistré : le chauffeur ne devrait avoir à le
            // chercher dans son gestionnaire de fichiers qu'une seule fois.
            const suggested = (file.name || 'Trajet').replace(/\.json$/i, '');
            saveTrip(suggested, data, { syncId: newSyncId() }).then(renderTripList).catch(() => {});
            startNavigation(data);
        } catch (err) { alert("Fichier JSON invalide."); }
    };
    reader.readAsText(file);
});

// drawPlannedRoute() et getNavigationInstruction() ont été remplacées par
// buildNavigationRoute() + updateGuidance() (voir le MOTEUR DE GUIDAGE plus haut).

exportBtn.addEventListener('click', generateRouteInstructions);
closeBtn.addEventListener('click', () => instructionsModal.classList.add('hidden'));

// Nouvelle logique de téléchargement centralisée
function downloadFile(content, fileName, mimeType) {
    const blob = new Blob([content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    }, 100);
}

saveJsonBtn.addEventListener('click', () => {
    if (markedStops.length === 0) return;

    // Enregistrement dans l'app, en plus du fichier : c'est ce qui alimente la liste du menu.
    const suggested = `Tournée du ${new Date().toLocaleDateString('fr-CA')}`;
    const name = prompt("Nom de la tournée :", suggested);
    // Tout est dans ce bloc : prompt annulé, rien n'est enregistré ni envoyé — mais la
    // modale JSON et le téléchargement du fichier partent quand même, comme avant.
    if (name) {
        const finalName = name.trim() || suggested;
        const stops = markedStops.slice();
        const syncId = newSyncId();
        saveTrip(finalName, stops, { syncId })
            .then(localId => {
                logEvent("Système", `Tournée « ${finalName} » enregistrée.`);
                renderTripList();
                // Envoi au serveur : jamais bloquant, le trajet est déjà en sécurité en
                // local. On ne redemande PAS le code ici — s'insérer entre le prompt du nom
                // et la modale JSON serait le pire moment ; la saisie se fait sur l'écran
                // des tournées.
                return pushTrip(localId, finalName, stops, syncId)
                    .then(() => {
                        logEvent("Serveur", `Tournée « ${finalName} » envoyée.`);
                        renderTripList();
                    })
                    .catch(e => {
                        if (e instanceof ApiAuthError) {
                            logEvent("Serveur", "Code d'accès requis — la tournée reste sur l'appareil.");
                        } else if (e instanceof ApiOfflineError) {
                            logEvent("Serveur", "Injoignable — la tournée reste sur l'appareil.");
                        } else {
                            logEvent("Serveur", "Envoi refusé : " + e.message);
                        }
                    });
            })
            .catch(e => logEvent("Erreur", "Enregistrement local impossible : " + e.message));
    }

    const dataStr = JSON.stringify(markedStops, null, 2);
    
    // Afficher le modal avec le code
    jsonOutput.value = dataStr;
    jsonModal.classList.remove('hidden');
    
    // Tenter le téléchargement automatique
    downloadFile(dataStr, `Trajet_Bus_${new Date().toISOString().slice(0,10)}.json`, 'application/json');
    logEvent("Système", "Tentative de sauvegarde du fichier JSON.");
});

// Fermer le modal JSON
closeJsonBtn.addEventListener('click', () => jsonModal.classList.add('hidden'));

// Copier le JSON dans le presse-papier
copyJsonBtn.addEventListener('click', () => {
    jsonOutput.select();
    document.execCommand('copy');
    
    const originalText = copyJsonBtn.innerHTML;
    copyJsonBtn.innerHTML = '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg> Copié !';
    copyJsonBtn.classList.replace('text-slate-700', 'text-emerald-600');
    
    setTimeout(() => {
        copyJsonBtn.innerHTML = originalText;
        copyJsonBtn.classList.replace('text-emerald-600', 'text-slate-700');
    }, 2000);
});

// Forcer le téléchargement depuis le modal
forceDownloadBtn.addEventListener('click', () => {
    downloadFile(jsonOutput.value, `Trajet_Bus_${new Date().toISOString().slice(0,10)}.json`, 'application/json');
});

async function generateRouteInstructions() {
    if (markedStops.length < 2) {
        alert("Erreur: Vous devez enregistrer au moins un départ et un arrêt pour générer un trajet.");
        return;
    }

    instructionsModal.classList.remove('hidden');
    instructionsContent.innerHTML = `
        <div class="flex flex-col items-center justify-center h-full space-y-4">
            <div class="animate-spin rounded-full h-12 w-12 border-4 border-indigo-200 border-t-indigo-600"></div>
            <span class="text-slate-500 font-medium">Analyse des routes empruntées...</span>
        </div>`;

    // Construire la chaîne pour OSRM
    const coordsString = markedStops.map(s => `${s.lng},${s.lat}`).join(';');
    
    // ASTUCE CRUCIALE : radiuses=unlimited permet à OSRM d'accepter des points GPS 
    // qui sont dans des stationnements ou loin de la rue (sinon ça plante).
    const radiuses = markedStops.map(() => 'unlimited').join(';');
    const url = `https://router.project-osrm.org/route/v1/driving/${coordsString}?steps=true&overview=false&radiuses=${radiuses}`;

    try {
        const response = await fetch(url);
        const data = await response.json();

        if (data.code === 'Ok' && data.routes && data.routes.length > 0) {
            renderInstructions(data.routes[0].legs);
        } else if (data.code === 'NoRoute') {
            showError("Impossible de trouver une route entre ces points. Sont-ils trop éloignés ou en dehors du réseau routier ?");
        } else {
            showError(`Erreur du serveur de routage : ${data.message || data.code}`);
        }
    } catch (error) {
        console.error(error);
        showError("Erreur de connexion. Vérifiez votre connexion internet ou réessayez plus tard (le serveur public OSRM peut être surchargé).");
    }
}

function showError(msg) {
    instructionsContent.innerHTML = `
        <div class="bg-red-50 p-4 rounded-xl border border-red-200 text-red-700">
            <svg class="w-8 h-8 mb-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
            <p class="font-bold mb-1">Impossible de générer le trajet</p>
            <p class="text-sm">${msg}</p>
        </div>`;
}

function renderInstructions(legs) {
    let html = '<div class="space-y-6 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-indigo-100 before:to-indigo-50">';
    let textOutput = "=== INSTRUCTIONS DU TRAJET D'AUTOBUS ===\nDate: " + new Date().toLocaleDateString('fr-CA') + "\n\n";

    legs.forEach((leg, index) => {
        const distanceKm = (leg.distance / 1000).toFixed(2);
        
        html += `
            <div class="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
                <div class="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-indigo-500 text-white font-bold text-sm shadow shrink-0 z-10 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2">
                    ${index + 1}
                </div>
                <div class="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-white p-4 rounded-xl shadow-sm border border-slate-100">
                    <h4 class="font-bold text-indigo-900 text-sm mb-2 pb-2 border-b border-slate-100">
                        Vers Arrêt ${index + 1} <span class="text-slate-400 font-normal text-xs ml-2">(${distanceKm} km)</span>
                    </h4>
                    <ul class="space-y-2.5">
        `;
        textOutput += `\n--- VERS ARRÊT ${index + 1} (${distanceKm} km) ---\n`;

        leg.steps.forEach(step => {
            if (step.distance === 0 && step.maneuver.type !== 'arrive' && step.maneuver.type !== 'depart') return;
            
            const instructionFr = translateInstruction(step);
            const distStr = step.distance > 0 ? (step.distance > 1000 ? (step.distance/1000).toFixed(1) + ' km' : Math.round(step.distance) + ' m') : '';
            
            html += `
                <li class="flex gap-3 items-start text-sm text-slate-600">
                    <span class="mt-1 text-slate-300 w-4 h-4 shrink-0 flex items-center justify-center">
                        <svg fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clip-rule="evenodd"></path></svg>
                    </span>
                    <span><span class="font-medium text-slate-800">${instructionFr}</span> ${distStr ? `<br><span class="text-xs text-slate-400">sur ${distStr}</span>` : ''}</span>
                </li>`;
            
            textOutput += `- ${instructionFr} ${distStr ? '(sur ' + distStr + ')' : ''}\n`;
        });

        html += `</ul></div></div>`;
    });

    html += '</div>';
    instructionsContent.innerHTML = html;
    rawGeneratedInstructions = textOutput;
}

downloadBtn.addEventListener('click', () => {
    if (!rawGeneratedInstructions) return;
    downloadFile(rawGeneratedInstructions, `Instructions_Autobus_${new Date().toISOString().slice(0,10)}.txt`, 'text/plain');
});

// Conservée pour la modale d'export (renderInstructions) : toute la logique FR vit
// maintenant dans describeManeuver(), partagée avec le bandeau de guidage.


// =====================================================================
//  AMORÇAGE
// =====================================================================

// Éléments introduits par le PWA
const tripScreen = document.getElementById('trip-screen');
const tripList = document.getElementById('trip-list');
const btnTripsBack = document.getElementById('btn-trips-back');
const btnImportJson = document.getElementById('btn-import-json');
// La carte « Carte hors ligne » existe en deux exemplaires (menu principal et écran des
// tournées). On les pilote ensemble : sinon celui qu'on ne regarde pas afficherait
// « Télécharger » pendant que 17 Mo transitent.
const mapCards = Array.from(document.querySelectorAll('[data-map-card]')).map(card => ({
    status: card.querySelector('[data-map-status]'),
    btn: card.querySelector('[data-map-btn]'),
    wrap: card.querySelector('[data-map-progress-wrap]'),
    bar: card.querySelector('[data-map-progress]')
}));
const serverStatus = document.getElementById('server-status');
const btnServerCode = document.getElementById('btn-server-code');
const btnNextStop = document.getElementById('btn-next-stop');
const btnUpdate = document.getElementById('btn-update');
const buildStamp = document.getElementById('build-stamp');

// La vue du guidage : seul endroit où la machine à états touche au DOM.
const guidanceView = {
    // turn() ne masque PLUS la pancarte : les deux cohabitent désormais, et c'est hideStop()
    // qui la retire. Le chauffeur doit voir ce qui vient après pendant l'embarquement.
    turn({ iconKey, distance, street, action }) {
        navTurn.classList.remove('hidden');
        // On ne réécrit le SVG que si l'icône change, sinon le navigateur reconstruit le
        // tracé à chaque seconde pour rien.
        if (navArrow.dataset.key !== iconKey) {
            navArrow.innerHTML = MANEUVER_ICONS[iconKey] || MANEUVER_ICONS['straight'];
            navArrow.dataset.key = iconKey;
        }
        navDistance.textContent = distance;
        navStreet.textContent = street;
        // L'action est la ligne PRINCIPALE du bandeau (« TOURNER À GAUCHE »), la rue vient en
        // dessous. L'inverse reléguait l'instruction en 11 px.
        navText.textContent = action;
    },
    stop({ distance, label }) {
        navStop.classList.remove('hidden');
        navStopDist.textContent = distance;
        navStopLabel.textContent = label;
    },
    hideStop() { navStop.classList.add('hidden'); },
    // textContent et jamais innerHTML : un nom d'arrêt vient d'un autre appareil.
    stopAhead({ label, distance }) {
        navStopAheadLabel.textContent = label || '\u00a0';
        navStopAheadDist.textContent = distance;
    },
    finished() {
        navStop.classList.add('hidden');
        navTurn.classList.remove('hidden');
        navArrow.innerHTML = MANEUVER_ICONS['arrive'];
        navArrow.dataset.key = 'arrive';
        navDistance.textContent = '0 m';
        navStreet.textContent = 'Trajet terminé';
        navText.textContent = 'Terminé';
        // Sinon la ligne du prochain arrêt fige la dernière distance mesurée.
        navStopAheadLabel.textContent = '\u00a0';
        navStopAheadDist.textContent = '--';
    },
    error(msg) {
        navStop.classList.add('hidden');
        navTurn.classList.remove('hidden');
        navArrow.innerHTML = MANEUVER_ICONS['straight'];
        navArrow.dataset.key = 'straight';
        navDistance.textContent = '!';
        navStreet.textContent = msg;
        navText.textContent = 'Erreur de routage';
    },
    status(text) { navStreet.textContent = text; },
    route(coords, fit) {
        plannedRoutePolyline.setLatLngs(coords);
        if (fit && coords.length) {
            map.fitBounds(plannedRoutePolyline.getBounds(), { padding: [50, 50] });
        }
    }
};

// Les modules ne lisent aucune globale : on leur fournit leurs dépendances ici.
initGuidance({ view: guidanceView, log: logEvent, getSpeedKmh: () => currentSpeedKmh });
setRoutingLogger(logEvent);
setWakeLockGuard(() => isTracking);
installWakeLockListeners();
initVoice({ btnTts, ttsIcon });

buildStamp.textContent = __BUILD_ID__;

// ---------------- Liste des trajets ----------------
//
// Deux étages volontairement séparés :
//   renderTripList()  — local seulement, jamais un octet de réseau, jamais bloquant ;
//   syncServerTrips() — interroge le serveur puis repeint.
// Les confondre ferait un appel réseau à chaque amorçage de l'application, et ferait
// attendre l'ouverture de l'écran des tournées le temps du délai d'attente.

let remoteTrips = null;   // dernière liste serveur connue ; null = jamais obtenue
let syncing = false;

function setServerStatus(text) { serverStatus.textContent = text; }

const BADGES = {
    local:  { text: 'Non envoyée', cls: 'bg-amber-100 text-amber-700' },
    synced: { text: 'Partagée',    cls: 'bg-slate-100 text-slate-500' },
    remote: { text: 'Sur le serveur', cls: 'bg-emerald-100 text-emerald-700' }
};

const ICON_UPLOAD = '<svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"></path></svg>';
const ICON_DOWNLOAD = '<svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path></svg>';
const ICON_TRASH = '<svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>';

// Envoi (ou renvoi) d'un trajet local. Rejouable sans risque : l'identifiant vient du
// client et le serveur fait un upsert, donc deux appels successifs ne créent jamais deux
// tournées. Renvoie l'identifiant partagé.
async function pushTrip(localId, name, stops, syncId) {
    const id = syncId || newSyncId();
    await apiPutTrip(id, { name, stops });
    await markTripSynced(localId, id);
    return id;
}

function tripRow(row) {
    const li = document.createElement('li');
    li.className = 'bg-white rounded-2xl shadow-sm border border-slate-100 flex items-stretch overflow-hidden';

    const open = document.createElement('button');
    open.className = 'flex-1 text-left p-4 hover:bg-slate-50 active:scale-[0.99] transition min-w-0';

    const title = document.createElement('p');
    title.className = 'font-bold text-slate-800 truncate';
    // textContent et NON innerHTML : un nom de tournée vient maintenant du serveur, donc
    // d'un autre appareil. Interpolé dans du HTML, il permettrait d'injecter du balisage
    // dans l'application du chauffeur. La validation du serveur ne remplace pas ça.
    title.textContent = row.name;

    const sub = document.createElement('p');
    sub.className = 'text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap';
    const when = new Date(row.createdAt);
    const stamp = document.createElement('span');
    // L'heure compte : deux appareils produisent souvent deux « Tournée du 23 » le même
    // jour, la date seule ne les distinguerait pas.
    stamp.textContent = `${row.stopCount} arrêt(s) · ${when.toLocaleDateString('fr-CA')} `
        + when.toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' });
    const badge = document.createElement('span');
    const b = BADGES[row.state];
    badge.className = `text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${b.cls}`;
    badge.textContent = b.text;
    sub.append(stamp, badge);

    open.append(title, sub);
    open.addEventListener('click', () => openRow(row));
    li.appendChild(open);

    // Trajet local jamais parvenu au serveur : bouton de renvoi.
    if (row.state === 'local' && row.localId !== null) {
        const send = document.createElement('button');
        send.className = 'px-3 text-slate-300 hover:text-blue-600 hover:bg-blue-50 transition';
        send.title = 'Envoyer au serveur';
        send.innerHTML = ICON_UPLOAD;
        send.addEventListener('click', () => resendRow(row, send));
        li.appendChild(send);
    }

    const del = document.createElement('button');
    del.className = 'px-4 text-slate-300 hover:text-rose-500 hover:bg-rose-50 transition';
    del.title = 'Supprimer';
    del.innerHTML = ICON_TRASH;
    del.addEventListener('click', () => deleteRow(row));
    li.appendChild(del);

    return li;
}

// Un trajet du serveur est d'abord rangé en local : la tournée doit rester praticable si
// le réseau tombe en cours de route.
async function openRow(row) {
    if (row.localId !== null) {
        // localId est le nombre auto-incrémenté d'IndexedDB, jamais l'identifiant serveur :
        // getTrip('<uuid>') renverrait undefined sans lever la moindre erreur.
        const full = await getTrip(row.localId);
        if (full && full.stops) startNavigation(full.stops);
        return;
    }
    try {
        const trip = await apiGetTrip(row.remoteId);
        await saveTrip(trip.name, trip.stops, { syncId: trip.id, remoteId: trip.id });
        logEvent("Serveur", `Tournée « ${trip.name} » récupérée.`);
        startNavigation(trip.stops);
        renderTripList();
    } catch (e) {
        alert(e instanceof ApiAuthError
            ? "Code d'accès refusé — vérifiez-le sur cet écran."
            : "Serveur injoignable — impossible de récupérer cette tournée.");
    }
}

async function resendRow(row, btn) {
    btn.disabled = true;
    try {
        const full = await getTrip(row.localId);
        if (!full || !full.stops) return;
        const id = await pushTrip(row.localId, full.name, full.stops, full.syncId || row.syncId);
        if (Array.isArray(remoteTrips)) {
            remoteTrips = remoteTrips.filter(t => t.id !== id)
                .concat([{ id, name: full.name, stopCount: full.stopCount, createdAt: full.createdAt }]);
        }
        logEvent("Serveur", `Tournée « ${full.name} » envoyée.`);
        renderTripList();
    } catch (e) {
        btn.disabled = false;
        alert(e instanceof ApiAuthError
            ? "Code d'accès refusé — vérifiez-le sur cet écran."
            : "Serveur injoignable — la tournée reste sur l'appareil.");
    }
}

async function deleteRow(row) {
    const onServer = !!row.remoteId;
    const onDevice = row.localId !== null;
    // La formulation dit exactement ce qui disparaît : supprimer une tournée partagée la
    // retire pour tous les chauffeurs, ce n'est pas la même chose qu'un ménage local.
    const ou = onServer && onDevice ? 'du serveur ET de cet appareil'
             : onServer ? 'du serveur (pour tous les appareils)'
             : 'de cet appareil';
    if (!confirm(`Supprimer « ${row.name} » ${ou} ?`)) return;

    if (onServer) {
        try {
            await apiDeleteTrip(row.remoteId);
        } catch (e) {
            // Rien n'est supprimé en local si le serveur a refusé : sinon la tournée
            // disparaîtrait de l'appareil en restant partagée, sans moyen de le voir.
            alert(e instanceof ApiAuthError
                ? "Code d'accès refusé — suppression annulée."
                : "Serveur injoignable — suppression annulée.");
            return;
        }
        // Retrait ciblé plutôt qu'un remoteTrips = null : remettre la liste à « inconnue »
        // ferait clignoter toutes les autres lignes en « Non envoyée ».
        if (Array.isArray(remoteTrips)) remoteTrips = remoteTrips.filter(t => t.id !== row.remoteId);
    }
    if (row.localId !== null) await deleteTrip(row.localId);
    renderTripList();
}

async function renderTripList() {
    let local = [];
    try { local = await listTrips(); } catch (e) { /* stockage indisponible */ }
    const rows = mergeTripLists(local, remoteTrips);
    tripList.innerHTML = '';
    if (!rows.length) {
        tripList.innerHTML = '<li class="text-center text-slate-400 text-sm py-10">Aucune tournée enregistrée.<br>Importez un fichier JSON, ou enregistrez un trajet.</li>';
        return;
    }
    rows.forEach(r => tripList.appendChild(tripRow(r)));
}

async function syncServerTrips() {
    if (syncing) return;
    syncing = true;
    try {
        remoteTrips = await apiListTrips();
        setServerStatus(`Serveur : ${remoteTrips.length} tournée(s) partagée(s)`);
    } catch (e) {
        remoteTrips = null;
        if (e instanceof ApiAuthError) {
            setServerStatus(getCode() ? "Code d'accès refusé" : "Code d'accès requis");
        } else {
            setServerStatus('Serveur injoignable — tournées locales seulement');
        }
    } finally {
        syncing = false;
    }
    renderTripList();
}

// --- Sauter un arrêt : deux appuis ---
// Le journal étant masqué, tout accusé de réception doit vivre DANS le bouton : c'est le seul
// endroit que le chauffeur regarde déjà, et le seul qu'il puisse lire sans quitter la route
// des yeux plus d'un instant.
const SKIP_BTN_HTML = '<span class="text-base font-black uppercase tracking-wide">Sauter</span>'
    + '<span class="text-xs font-bold uppercase tracking-wide opacity-90">l\'arrêt</span>';
const SKIP_BTN_CLASS = 'flex-1 bg-amber-500 hover:bg-amber-600 active:bg-amber-700 text-white'
    + ' font-bold py-3.5 px-4 rounded-2xl shadow-md transition-transform active:scale-[0.98]'
    + ' flex flex-col items-center justify-center leading-tight';

function disarmSkip() {
    clearTimeout(skipArmTimer);
    skipArmTimer = null;
    skipArmed = false;
    btnSkipStop.innerHTML = SKIP_BTN_HTML;
    btnSkipStop.className = SKIP_BTN_CLASS;
}

function setSkipBtn(html, extraClass, ms) {
    btnSkipStop.innerHTML = html;
    btnSkipStop.className = SKIP_BTN_CLASS.replace(/bg-amber-\S+|hover:bg-amber-\S+|active:bg-amber-\S+/g, '')
        + ' ' + extraClass;
    clearTimeout(skipArmTimer);
    skipArmTimer = setTimeout(disarmSkip, ms);
}

btnSkipStop.addEventListener('click', () => {
    const cur = nav.stopPoints[nav.stopIndex];
    if (!cur) {
        setSkipBtn('<span class="text-sm font-black uppercase">Aucun arrêt</span>',
                   'bg-slate-400', 1500);
        return;
    }
    if (!currentPosition) {
        // alert() bloquerait l'écran d'un chauffeur qui roule : le message reste dans le bouton.
        setSkipBtn('<span class="text-sm font-black uppercase">Sans GPS</span>', 'bg-slate-400', 2000);
        return;
    }

    if (!skipArmed) {
        skipArmed = true;
        setSkipBtn('<span class="text-sm font-black uppercase leading-none">Confirmer ?</span>'
                   + `<span class="text-xs font-bold mt-1 px-1 text-center">${cur.label}</span>`,
                   'bg-red-600', SKIP_ARM_MS);
        try { if (navigator.vibrate) navigator.vibrate(30); } catch (e) {}
        return;
    }

    if (!skipStop(currentPosition.lat, currentPosition.lng)) { disarmSkip(); return; }
    skipArmed = false;
    try { if (navigator.vibrate) navigator.vibrate([40, 60, 40]); } catch (e) {}
    setSkipBtn('<span class="text-2xl font-black leading-none">✓</span>'
               + '<span class="text-xs font-bold uppercase mt-1">Sauté</span>', 'bg-slate-500', 1200);
    // Rafraîchissement immédiat : attendre le prochain fix laisserait la pancarte à l'écran.
    updateGuidance(currentPosition.lat, currentPosition.lng);
    // Doublure du retour, APRÈS updateGuidance : renderTurn() réécrit la ligne de rue à
    // chaque passage, un status() posé avant serait effacé dans la milliseconde.
    guidanceView.status(`${cur.label} sauté`);
});


// Confirmation d'embarquement. Réaffichage immédiat : attendre le prochain fix GPS laisserait
// la pancarte à l'écran plusieurs secondes après le tap, et le chauffeur taperait une 2e fois.
btnNextStop.addEventListener('click', () => {
    if (!confirmStop()) return;
    try { if (navigator.vibrate) navigator.vibrate(50); } catch (e) {}
    if (currentPosition) updateGuidance(currentPosition.lat, currentPosition.lng);
    else guidanceView.hideStop();
});

btnServerCode.addEventListener('click', () => {
    const entered = prompt("Code d'accès au serveur :", getCode());
    if (entered === null) return;
    // Changer le code invalide l'ancien : c'est un secret partagé pour toute la flotte.
    setCode(entered.trim());
    setServerStatus('Vérification du serveur…');
    syncServerTrips();
});

btnTripsBack.addEventListener('click', () => {
    tripScreen.classList.add('hidden');
    tripScreen.classList.remove('flex');
});
btnImportJson.addEventListener('click', () => fileInput.click());

// ---------------- Carte hors ligne ----------------

function formatMo(bytes) { return (bytes / 1e6).toFixed(1) + ' Mo'; }

let mapDownloading = false;

async function refreshMapStatus() {
    const st = await basemapStatus();
    for (const c of mapCards) {
        if (st.offline) {
            c.status.textContent = `Disponible hors ligne (${formatMo(st.bytes)})`;
            c.btn.textContent = 'À jour';
        } else {
            c.status.textContent = 'Non téléchargée — la carte exige du réseau';
            c.btn.textContent = 'Télécharger';
        }
        c.btn.disabled = st.offline;
        // toggle et non add : sans le retrait, un échec après un premier succès laissait le
        // bouton vert « À jour » sur une carte absente.
        c.btn.classList.toggle('bg-emerald-600', st.offline);
    }
}

async function downloadMap() {
    // Les deux cartes visent le même fichier : deux téléchargements simultanés de 17 Mo
    // n'apporteraient rien qu'une facture de données doublée.
    if (mapDownloading) return;
    mapDownloading = true;

    for (const c of mapCards) {
        c.btn.disabled = true;
        c.btn.textContent = '…';
        c.wrap.classList.remove('hidden');
    }
    try {
        // La persistance évite qu'iOS évince le fichier après quelques jours d'inactivité.
        await requestPersistentStorage();
        const size = await downloadBasemap((recu, total) => {
            const pct = total ? Math.round(recu / total * 100) : 0;
            const text = `Téléchargement… ${formatMo(recu)}${total ? ' / ' + formatMo(total) : ''}`;
            // Progression poussée dans TOUTES les cartes : celle qu'on ne regarde pas ne
            // doit pas afficher « Télécharger » pendant le transfert.
            for (const c of mapCards) {
                c.bar.style.width = pct + '%';
                c.status.textContent = text;
            }
        });
        for (const c of mapCards) {
            c.status.textContent = `Téléchargée (${formatMo(size)}) — redémarrez pour l'utiliser`;
        }
        logEvent("Carte", `Fond de carte régional enregistré (${formatMo(size)}).`);
    } catch (e) {
        for (const c of mapCards) {
            c.status.textContent = 'Échec du téléchargement : ' + e.message;
            c.btn.disabled = false;
            c.btn.textContent = 'Réessayer';
        }
        mapDownloading = false;
        return;
    }
    mapDownloading = false;
    for (const c of mapCards) c.wrap.classList.add('hidden');
    refreshMapStatus();
}

mapCards.forEach(c => c.btn.addEventListener('click', downloadMap));

// ---------------- Mise à jour de version ----------------

btnUpdate.addEventListener('click', () => {
    // Jamais pendant une tournée : le bouton n'est proposé qu'au menu, et on refuse net
    // si un suivi GPS est actif.
    if (isTracking) {
        alert("Terminez la tournée avant de mettre à jour.");
        return;
    }
    applyUpdate();
});

registerServiceWorker(() => {
    btnUpdate.classList.remove('hidden');
    logEvent("Système", "Nouvelle version prête, en attente de redémarrage.");
});

// On ne cherche une mise à jour que hors suivi GPS.
document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && !isTracking) checkForUpdate();
});

// ---------------- Départ ----------------

// Le bundle est chargé en defer : le DOM est prêt, pas besoin d'attendre 'load'.
initMap();
refreshMapStatus();
// Local seulement : l'amorçage ne doit toucher au réseau ni pour le serveur ni pour la
// carte. Le serveur n'est interrogé qu'à l'ouverture de l'écran des tournées.
renderTripList();
